/**
 * ly-carousel.js
 * Scroll-snap carousel with dot pagination for any section that has:
 *   [data-carousel-grid]  — the scrollable cards container
 *   [data-carousel-dots]  — the dots wrapper (data-active-class="<prefix>__dot--active")
 *
 * Listeners attach only at viewports where the mobile carousel layout is active
 * (max-width: 699px), matching the section CSS breakpoints.
 *
 * The grids are not made keyboard-focusable: keyboard users navigate via the
 * pagination dots (which scroll the grid on activation) and via the focusable
 * links inside cards. The grid keeps role="group" + aria-label in markup for a
 * labelled, non-focusable AT grouping.
 *
 * Theme editor: listens for shopify:section:load so carousels re-wire when a
 * section's HTML is replaced (external scripts do not re-run on section refresh).
 *
 * Include once from the layout; each grid is only wired once, but the script
 * registers document-level listeners, so duplicate tags would stack handlers.
 */
(function () {
  'use strict';

  const MQ = '(max-width: 699px)';

  function getMq() {
    return window.matchMedia(MQ);
  }

  function initCarousel(grid) {
    if (grid.dataset.carouselInit === '1') return;
    grid.dataset.carouselInit = '1';

    const section = grid.closest('.shopify-section') || grid.parentElement;
    const dotsContainer = section && section.querySelector('[data-carousel-dots]');
    if (!dotsContainer) return;

    const activeClass = dotsContainer.dataset.activeClass || 'active';
    const dots = Array.from(dotsContainer.querySelectorAll('[data-carousel-dot]'));
    const cards = Array.from(grid.children);

    function cardStepWidth() {
      const gap = parseFloat(getComputedStyle(grid).gap) || 0;
      return cards.length > 0 ? cards[0].getBoundingClientRect().width + gap : 0;
    }

    function getActiveIndex() {
      const w = cardStepWidth() || 1;
      return Math.round(grid.scrollLeft / w);
    }

    function updateDots(idx) {
      dots.forEach((dot, i) => {
        const isActive = i === idx;
        dot.classList.toggle(activeClass, isActive);
        dot.setAttribute('aria-current', String(isActive));
      });
    }

    grid.addEventListener(
      'scroll',
      () => {
        updateDots(getActiveIndex());
      },
      { passive: true }
    );

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const w = cardStepWidth();
        grid.scrollTo({ left: w * i, behavior: 'smooth' });
      });
    });
  }

  function tryInitAll() {
    if (typeof window.matchMedia === 'function' && !getMq().matches) return;
    document.querySelectorAll('[data-carousel-grid]').forEach(initCarousel);
  }

  function onMqChange() {
    tryInitAll();
  }

  function boot() {
    tryInitAll();
    if (typeof window.matchMedia === 'function') {
      const mq = getMq();
      if (mq.addEventListener) mq.addEventListener('change', onMqChange);
      else if (mq.addListener) mq.addListener(onMqChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (ev) => {
    const root = ev.target;
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (typeof window.matchMedia === 'function' && !getMq().matches) return;
    root.querySelectorAll('[data-carousel-grid]').forEach(initCarousel);
  });
})();
